import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry error tracking for the frontend.
 * Only initializes in production or when VITE_SENTRY_DSN is set.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Performance monitoring - 10% in prod, 100% in dev for testing
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay for debugging user issues
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking - Vercel provides git commit SHA
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || undefined,

    // Filter out noisy errors that aren't actionable
    ignoreErrors: [
      // Browser extensions injecting errors
      'top.GLOBALS',
      // ResizeObserver loop errors (benign)
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      // Network errors users can't do anything about
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      // AbortController cancellations (intentional)
      'AbortError',
      'The operation was aborted',
      // Browser-specific quirks
      'Non-Error promise rejection captured',
    ],

    // Enable whenever DSN is provided (DSN should only be set in deployed environments)
    enabled: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Don't send PII by default
    sendDefaultPii: false,
  })
}

/**
 * Capture an exception with optional extra context.
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Capture a message (non-error event).
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level)
}

/**
 * Set user context for error tracking.
 * Call this after user authentication.
 */
export function setUser(user: { id: string; email?: string; tenantId?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    // Custom fields
    ...( user.tenantId && { tenantId: user.tenantId }),
  } as Sentry.User)
}

/**
 * Clear user context on logout.
 */
export function clearUser() {
  Sentry.setUser(null)
}

/**
 * Add breadcrumb for debugging context.
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

export { Sentry }
